import PlanIcon from "app/assets/IconComponent/PlanIocn";
import HexagonMetricsIcon from "app/assets/IconComponent/HexagonMetricsIcon";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import { ProgressBar } from "primereact/progressbar";

interface PhaseButton {
  label: string;
  current: number;
  total: number;
  progress?: number; // 0-100 — uses (current/total)*100 as fallback
  isActive?: boolean;
}

interface PlanPhasesCardProps {
  // planType?: string;
  effectiveDate?: string;
  progressPercent?: number; // kept for legacy callers
  phases?: PhaseButton[];
  planSubType?: string;
}

const MOCK_PLAN: PlanPhasesCardProps = {
  // planType: "Emerging Affluent",
  planSubType: "Emerging Affluent",
  effectiveDate: "Sep 25, 2024",
  progressPercent: 20,
  phases: [
    { label: "FUNDING", current: 2, total: 10, progress: 20, isActive: true },
    { label: "GROWTH", current: 0, total: 5, progress: 0, isActive: false },
  ],
};

const PlanPhasesCard = ({
  // planType = MOCK_PLAN.planType,
  effectiveDate = MOCK_PLAN.effectiveDate,
  progressPercent = MOCK_PLAN.progressPercent!,
  planSubType = MOCK_PLAN.planSubType,
  phases = MOCK_PLAN.phases!,
}: PlanPhasesCardProps) => {
  const globalTheme = getSessionStore("globalTheme");
  const isDark = globalTheme === THEME.DARK;

  const cardBase = `h-full rounded-2xl overflow-hidden shadow-lg py-1 ${
    isDark ? "bg-[#2A2A3D]" : "bg-white"
  }`;

  const labelClass = `text-[11px] font-medium uppercase tracking-wide ${
    isDark ? "text-gray-400" : "text-[#6B7280]"
  }`;

  const valueClass = `text-[15px] font-bold conthrax-font mt-[2px] ${
    isDark ? "text-white" : "text-[#0F172A]"
  }`;

  const sectionHeaderClass = `text-[13px] p-4 font-semibold conthrax-font flex items-center gap-2 mb-3 bg-[#F5F5F5] ${
    isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]"
  }`;

  const fundingPhase = phases.find((p) => p.label.toUpperCase() === "FUNDING") ?? phases[0];
  const growthPhase = phases.find((p) => p.label.toUpperCase() === "GROWTH") ?? phases[1];

  const phaseProgress = (ph: PhaseButton) =>
    ph?.progress ?? (ph?.total ? Math.floor((ph.current / ph.total) * 100) : progressPercent ?? 0);

  const formatPlanSubType = (value: string) => {
    if (!value) return "";
    // Remove TYPE_1_, TYPE_2_, TYPE_3_ prefix and format the rest
    const withoutPrefix = value.replace(/^TYPE_\d+_/, "");
    return withoutPrefix
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <div className="flex gap-4 w-full">
      {/* ── Plan card ── */}
      <div className={`${cardBase} w-1/2 !pb-[54px]`}>
        <p className={sectionHeaderClass}>
          <PlanIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} size="16px" />
          Plan
        </p>

        <div className="flex gap-6 px-4 py-1">
          <div>
            <p className={labelClass}>Type</p>
            <p className={valueClass}>{planSubType ? formatPlanSubType(planSubType) : ""}</p>
          </div>
          <div>
            <p className={labelClass}>Effective Date</p>
            <p className={valueClass}>{effectiveDate}</p>
          </div>
        </div>
      </div>

      {/* ── Phases card ── */}
      <div className={`${cardBase} w-1/2`}>
        <p className={sectionHeaderClass}>
          <HexagonMetricsIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} width={16} height={16} />
          Phases
        </p>

        <div className="w-full flex flex-row gap-3 px-4 py-1 align-items-center">
          {/* Funding */}
          <div className="w-8 flex flex-column">
            <div className="w-full mb-2 border-1 border-blue-700 p-1 rounded-lg">
              <ProgressBar
                value={phaseProgress(fundingPhase)}
                className="text-blue-700 h-2rem"
                pt={{ value: { style: { background: "#1E3A5F" } } }}
              />
            </div>
            <p className="sans-font font-semibold uppercase border-1 text-blue-500 border-blue-200 text-sm bg-blue-50 py-2 text-center rounded-lg">
              Funding {fundingPhase?.current ?? 0}/{fundingPhase?.total ?? 0}
            </p>
          </div>

          {/* Growth */}
          <div className="w-4 flex flex-column">
            <div className="w-full mb-2 border-1 border-gray-500 p-1 rounded-lg">
              <ProgressBar
                value={phaseProgress(growthPhase)}
                className="text-blue-700 h-2rem"
                pt={{ value: { style: { background: "#1E3A5F" } } }}
              />
            </div>
            <p className="sans-font font-semibold uppercase border-1 text-gray-500 border-gray-500 text-sm bg-gray-50 py-2 text-center rounded-lg">
              Growth {growthPhase?.current ?? 0}/{growthPhase?.total ?? 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanPhasesCard;
